<script lang="ts">
  import { SecurityFindingKind } from "$lib/models/base";
  import type { SecurityFinding } from "$lib/stores/orgSecurityFindings";
  import { toTitleCase } from "$lib/utils/text";
  import Button from "$lib/ui/button/button.svelte";
  import { DrawerHeader, DrawerTitle } from "$lib/ui/drawer";
  import UserList from "$lib/ui/user-list/user-list.svelte";
  import FindingFeedbackForm from "./FindingFeedbackForm.svelte";
  import FindingStatusUpdateForm from "./FindingStatusUpdateForm.svelte";
  import SeverityBadge from "./SeverityBadge.svelte";
  import SystemLogo, { type System } from "./SystemLogo.svelte";
  import ResourceTypeLogo from "./ResourceTypeLogo.svelte";
  import ImpactBadge from "./ImpactBadge.svelte";
  import { validateEmail } from "$lib/utils/email";
  import { toaster } from "$lib/utils/toaster";
  import * as Tooltip from "$lib/ui/tooltip";

  const { finding }: { finding: SecurityFinding | null } = $props();

  function securityFindingKindDescription(securityFindingKind: SecurityFindingKind | undefined) {
    switch (securityFindingKind) {
      case SecurityFindingKind.PUBLIC_DOCUMENT:
        return "Public document";
      case SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT:
        return "Anomalous permission(s) on sensitive document";
      case SecurityFindingKind.LARGE_SCALE_COLLABORATION_ON_PERSONAL_DRIVE:
        return "Large scale collaboration on personal drive";
      case SecurityFindingKind.PERMISSIONS_MANAGED_THROUGH_SECONDARY_IDENTITY:
        return "Permissions managed through secondary identity";
      case SecurityFindingKind.PERSONAL_DRIVE_WITH_SENSITIVE_DOCUMENTS:
        return "Personal drive with sensitive documents";
      default:
        return "Issue";
    }
  }
  function formatDate(date: Date) {
    return date.toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  const ownerEmail = finding?.resources?.[0]?.ownerEmail;
  const hasOwnerEmail = ownerEmail !== undefined && ownerEmail.trim() !== "";
  const isOwnerEmailValid = validateEmail(ownerEmail ?? "");
  const sendEmailToOwner = (email: string | undefined) => {
    if (email && isOwnerEmailValid) {
      window.location.href = `mailto:${email}?subject=Issue detected on ${finding?.resources?.[0]?.name}`;
    } else {
      toaster.error("Error", "Owner email is not available or invalid.");
    }
  };
</script>

<DrawerHeader class="pt-8 px-6 pb-4 relative">
  <div class="max-w-[calc(100%-1rem)]">
    <DrawerTitle class="text-2xl font-semibold text-left break-words">
      Issue impacting <span class="break-all">
        {finding?.resources?.[0]?.name}
        {#if finding?.resources?.length > 1}
          (And {finding?.resources?.length - 1} more)
        {/if}
      </span>
    </DrawerTitle>
  </div>
</DrawerHeader>
<div class="px-6 flex-1 flex flex-col overflow-auto gap-y-4">
  {#if !finding}
    <div class="py-2 text-base">
      <p>Loading finding...</p>
    </div>
  {:else}
    <div class="py-2 text-base">
      {securityFindingKindDescription(finding?.securityFindingKind)} detected
      {#if finding?.created}
        on <strong>{formatDate(finding.created.toDate())}</strong>
      {:else}
        .
      {/if}
    </div>
    <div class="flex flex-col gap-2">
      <FindingFeedbackForm {finding} />
    </div>

    <div class="grid grid-cols-3 gap-y-2 divide-y divide-gray-200">
      <div class="col-span-3"></div>
      <!-- Finding ID -->
      <div class="py-3 font-semibold">Finding ID</div>
      <div class="flex items-center col-span-2">{finding?.securityFindingId}</div>

      <!-- Resource -->
      <div class="py-3 font-semibold">Resource</div>
      <div class="flex items-center col-span-2">
        <div class="flex gap-2 max-w-full min-h-[3rem] items-center">
          <div class="flex-shrink-0 w-12">
            <ResourceTypeLogo {finding} />
          </div>
          <div class="flex flex-col gap-1">
            <div class="break-all flex-1">{finding?.resources?.[0]?.name}</div>
            <p class="text-xs italic">{finding.securityFindingKind}</p>
          </div>
        </div>
      </div>

      <div class="py-3 font-semibold">Resource Owner</div>
      <div class="flex items-center col-span-2">
        {#if hasOwnerEmail}
          <button
            disabled={!isOwnerEmailValid}
            onclick={() => sendEmailToOwner(ownerEmail)}
            class="underline text-blue-500 cursor-pointer break-all"
          >
            {ownerEmail}
          </button>
        {:else}
          <span class="text-gray-500 cursor-default break-all">No owner email available</span>
        {/if}
      </div>

      <div class="py-3 font-semibold whitespace-nowrap">Location</div>
      <div class="break-words flex items-center col-span-2">
        <a
          href={finding?.resources?.[0]?.link}
          target="_blank"
          rel="noopener noreferrer"
          class="underline text-blue-500 cursor-pointer break-all"
        >
          {finding?.resources?.[0]?.path}
        </a>
      </div>

      <div class="py-3 font-semibold">Severity</div>
      <div class="flex items-center col-span-2">
        <SeverityBadge {finding} />
      </div>

      <div class="py-3 font-semibold">Impact</div>
      <div class="flex items-center col-span-2">
        <ImpactBadge {finding} />
      </div>

      <div class="py-3 font-semibold">Confidence</div>
      <div class="flex items-center col-span-2">
        {((finding?.confidence ?? 0) * 100).toFixed(0)}%
      </div>

      {#if finding?.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT}
        <div class="py-3 font-semibold">Impacted Users</div>
        <div class="col-span-2">
          Recommendation to revoke access for <strong>{finding?.numberOfImpactedPrincipals}</strong> user(s) out of
          <strong>{finding?.totalNumberOfPrincipals}</strong>
          (<strong
            >{(((finding?.numberOfImpactedPrincipals ?? 0) / (finding?.totalNumberOfPrincipals ?? 1)) * 100).toFixed(
              0,
            )}%</strong
          >)
        </div>

        <div class="py-3 font-semibold">Permissions</div>
        <div class="col-span-2">
          Recommendation to revoke <strong>{finding?.numberOfMisconfiguredPermissions}</strong> permission(s) out of
          <strong>{finding?.totalNumberOfPermissions} </strong>
          (<strong
            >{(
              ((finding?.numberOfMisconfiguredPermissions ?? 0) / (finding?.totalNumberOfPermissions ?? 1)) *
              100
            ).toFixed(0)}%</strong
          >)
        </div>
      {/if}

      {#if finding.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL}
        <div class="py-3 font-semibold">External Principals</div>
        <div class="col-span-2 flex items-center">
          <strong>{finding?.numberOfExternalPrincipals}</strong>&nbsp;external principals.
        </div>
      {/if}

      {#if finding.securityFindingKind === SecurityFindingKind.LARGE_SCALE_COLLABORATION_ON_PERSONAL_DRIVE}
        <div class="py-3 font-semibold">Documents</div>
        <div class="col-span-2">
          <strong>{finding?.numberOfDocumentsInPersonalDrive}</strong> documents in the personal drive
        </div>
      {/if}

      {#if finding.securityFindingKind === SecurityFindingKind.PERSONAL_DRIVE_WITH_SENSITIVE_DOCUMENTS}
        <div class="py-3 font-semibold">Documents</div>
        <div class="col-span-2">
          <strong>{finding?.totalNumberOfDocuments}</strong> documents in the personal drive.
          <strong>{finding?.totalNumberOfSensitiveDocuments}</strong> of which are sensitive.
        </div>
      {/if}

      {#if finding.securityFindingKind === SecurityFindingKind.PERMISSIONS_MANAGED_THROUGH_SECONDARY_IDENTITY}
        <div class="py-3 font-semibold">Permissions</div>
        <div class="col-span-2">
          <strong>{finding?.totalNumberOfPermissions}</strong> permissions.
        </div>
      {/if}

      <div class="py-3 font-semibold">Source</div>
      <div class="flex items-center col-span-2">
        <SystemLogo system={finding?.system! as System} />
      </div>

      <div class="py-3 font-semibold whitespace-nowrap">Status</div>
      <div class="flex items-center col-span-2">
        <div class="p-2 w-full">
          <FindingStatusUpdateForm {finding} />
        </div>
      </div>
    </div>

    <!-- Recommended Actions  only supported for the following security finding kinds -->
    {#if finding.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT || finding.securityFindingKind === SecurityFindingKind.PUBLIC_DOCUMENT || finding.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL}
      <h2 class="font-semibold text-lg mt-3">Recommended Actions</h2>
      <!--Recommended Actions-->

      <div class="flex flex-col gap-2 text-md">
        <ul class="list-disc list-inside">
          {#if finding.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT}
            <li>
              Review access on the <a
                href={finding.resources[0].link}
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue-500 underline">document</a
              >, and ensure there are valid business reasons for each impacted user to access it. If not, revoke access.
            </li>
          {/if}
          {#if finding.securityFindingKind === SecurityFindingKind.PUBLIC_DOCUMENT}
            <li>
              This <a
                href={finding.resources[0].link}
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue-500 underline">document</a
              >
              is publicly accessible.
              {#if finding.isSensitiveDocument}
                This document contains sensitive information
                {#if finding.sensitiveTopics?.length > 0}
                  related to {#each finding.sensitiveTopics as topic, i}
                    <strong>{toTitleCase(topic.sensitiveTopic)}</strong
                    >{#if i < finding.sensitiveTopics.length - 1},&nbsp;{/if}
                  {/each}.
                {/if}
                and should not be shared with external parties.
              {/if}
              Please review whether it is intended and manage access accordingly.
            </li>
          {/if}
          {#if finding.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL}
            <li>
              Review access on the <a
                href={finding.resources[0].link}
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue-500 underline">document</a
              >, and ensure there are valid business reasons for each external user accessing it. If not, revoke access.
            </li>
          {/if}

          <li>
            You can contact the document owner <button
              disabled={!isOwnerEmailValid}
              onclick={() => sendEmailToOwner(ownerEmail)}
              class="text-blue-500 underline"
            >
              {ownerEmail}
            </button>
            for further actions.
          </li>

          {#if finding.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT}
            <li>
              Please note this document has been identified as containing sensitive information
              {#if finding.sensitiveTopics?.length > 0}
                related to {#each finding.sensitiveTopics as topic, i}
                  <strong>{toTitleCase(topic.sensitiveTopic)}</strong
                  >{#if i < finding.sensitiveTopics.length - 1},&nbsp;{/if}
                {/each}.
              {/if}
            </li>
          {/if}

          {#if finding.system === "sharepoint"}
            <li>
              To manage access to this document, <a
                href={finding.resources[0].link}
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue-500 underline">click here</a
              > to open it, click 'Share' on the top right, and then select 'Manage access'.
            </li>
            <li>
              Check how the user is granted access to the document: either directly, through a group, or through link
              sharing. Revoke the access if deemed necessary.
            </li>
          {/if}
        </ul>
      </div>
    {/if}

    <!-- Recommendations -->
    {#if finding.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT || finding.securityFindingKind === SecurityFindingKind.PUBLIC_DOCUMENT || finding.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL}
      <div class="flex flex-col gap-2">
        {#if finding.recommendations?.length && finding.recommendations.some((r) => (finding.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT && r.recommendedAction?.toLowerCase() === "revoke") || finding.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL)}
          <div class="flex-grow my-10">
            <h3 class="font-semibold text-lg mb-4">
              {#if finding.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT}
                Users with Anomalous Access
              {:else if finding.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL}
                External Users with Access
              {:else}
                Users with Access
              {/if}
            </h3>
            <UserList
              users={finding.recommendations
                .map((r) => {
                  if (
                    r.recommendedAction?.toLowerCase() === "revoke" ||
                    finding.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL
                  ) {
                    return r.email ?? "";
                  }
                  return "";
                })
                .filter((p) => p !== "")}
            />
          </div>
        {/if}

        <div class="flex flex-col gap-2">
          <Tooltip.Root>
            <Tooltip.Trigger>
              <Button class="w-full" disabled={!isOwnerEmailValid} onclick={() => sendEmailToOwner(ownerEmail)}>
                Contact Document Author
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>
              {#if isOwnerEmailValid}
                <p>{ownerEmail}</p>
              {:else}
                <p>No valid email available.</p>
              {/if}
            </Tooltip.Content>
          </Tooltip.Root>

          {#if finding?.recommendations?.length && finding?.recommendations.some((r) => r.recommended_action?.toLowerCase() === "revoke")}
            <Button
              href={`mailto:${finding?.recommendations
                ?.filter((r) => r.recommended_action?.toLowerCase() === "revoke" && r.email)
                .map((r) => r.email)
                .join(",")}?subject=Access to ${finding?.resources?.[0]?.name}`}
            >
              Notify Impacted Users
            </Button>
          {/if}
          <Button href={finding?.resources?.[0]?.link} target="_blank" rel="noopener noreferrer">
            View document and manage access
          </Button>
        </div>
      </div>
    {/if}
  {/if}
</div>
